import { queryOptions } from "@tanstack/react-query";
import {
    createDataSelector,
    createResourceCollectionSelector,
    createResourceSelector,
    handleJsonApiError,
    injectPageParams,
    type PageParams,
} from "jsonapi-zod-query";
import { z } from "zod";
import { apiUrl } from "@/utils/api.ts";

export const gateCodeAttributesSchema = z.object({
    id: z.number(),
    periodId: z.string(),
    startDt: z.string().transform((strDate) => {
        // new Date(strDate) results in timezone conversion issues
        const [year, month, day] = strDate.split("-").map(Number);
        return new Date(year, month - 1, day);
    }),
    endDt: z.string().transform((strDate) => {
        const [year, month, day] = strDate.split("-").map(Number);
        return new Date(year, month - 1, day);
    }),
    // I'm leaving this here bc it should be functionally equivalent and is much more fun
    // .transform((strDate) => new Date(...(strDate.split("-").map((v, i) => (i === 1 ? +v - 1 : +v)) as [number, number, number]))),
    code: z.string(),
});

const gateCodeSelector = createDataSelector(
    createResourceSelector({
        type: "gatecode",
        attributesSchema: gateCodeAttributesSchema,
    }),
);

const gateCodeCollectionSelector = createResourceCollectionSelector({
    type: "gatecode",
    attributesSchema: gateCodeAttributesSchema,
});

export type GateCode = ReturnType<typeof gateCodeSelector>;
export type PaginatedGateCodes = ReturnType<typeof gateCodeCollectionSelector>;
export type ListGateCodes = PaginatedGateCodes["data"][number];

export type ListGateCodesOptions = {
    pageParams?: PageParams;
};

export const createGateCodeQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (options?: ListGateCodesOptions) =>
        queryOptions({
            queryKey: ["gateCodes", options],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/gate-code");
                injectPageParams(url, options?.pageParams);

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return gateCodeCollectionSelector(await response.json());
            },
        }),
});
